import { gsap } from 'gsap';
import AbstractUnit from './AbstractUnit';
import { COLOR_THEMES } from '../../../../constants/colors';
import Text3D from '../../objects/Text3D';
import { SIZE_OPTIONS, UNIT } from '../../../../constants/size';
import LinesSet from '../../objects/LinesSet';
import { GeometriesFactory } from '../../factories/GeometriesFactory';
import { MaterialsFactory } from '../../factories/MaterialsFactory';
import { radians } from '../../../../utils/degreesAndRadians';

const THREE = require('three');

class FooterUnit extends AbstractUnit {
    _setup(data, stgs, webgl) {
        super._setup(data, stgs, webgl);

        const {
            theme, title, titlePlane, titlePrefix, titlePrefixPlane,
            linkText, linkTextPlane, action
        } = this.data;

        const {
            getTitleMetrics, getLinkMetrics,
            getPrefixMetrics
        } = stgs;

        this.hasResize = true;
        this.hasInvalidate = true;
        this.canFocusAt = true;
        this.actionKey = action.key;
        this._color = COLOR_THEMES[theme].text;
        this._title = titlePlane ?? title;
        this._titlePrefix = (titlePrefixPlane ?? titlePrefix ?? '').toLowerCase();
        this._link = (linkTextPlane ?? linkText ?? '').toLowerCase();
        this._getTitleMetrics = getTitleMetrics;
        this._getLinkMetrics = getLinkMetrics;
        this._getPrefixMetrics = getPrefixMetrics;

        this._webgl = webgl;
        this._amp = 0;
        this._hasOscillation = false;
        this._dioganalRatio = 1.2;

        // Default
        this._orientation = SIZE_OPTIONS.LANDSCAPE;
    }

    async _constructView(data, webgl, stgs, factory, orientation) {
        const sizesScale = UNIT.sizesScaleRatio;
        const root = new THREE.Group();

        const titleMesh = this._getTextMesh(
            this._getTitleMetrics(orientation),
            this._title
        );
        const titlePrefixMesh = this._getTextMesh(
            this._getPrefixMetrics(orientation),
            this._titlePrefix
        );

        const linkG = new THREE.Group();
        const linkFocusG = new THREE.Group();
        const linkMesh = this._getTextMesh(
            this._getLinkMetrics(orientation),
            this._link
        );
        const geom = await GeometriesFactory.create('rect', {
            width: 100,
            height: 100
        });
        const linkHitArea = new THREE.Mesh(geom, MaterialsFactory.create({
            transparent: true,
            opacity: 0
        }));

        const linesTop = new LinesSet(this._color, 125, 16, 0.5, 3);
        const linesBottom = new LinesSet(this._color, 125, 16, 0.5, 3);

        linesTop.scale.set(sizesScale, sizesScale, sizesScale);
        linesBottom.scale.set(sizesScale, sizesScale, sizesScale);
        linesBottom.rotation.z = Math.PI;
        linkHitArea.position.z = 0.006 * UNIT.zSize;

        linkMesh.renderOrder = 0;
        linkFocusG.renderOrder = 0;
        linkHitArea.renderOrder = 2;

        root.position.z = -this.mainViewZ;

        linkFocusG.add(linkMesh);
        linkG.add(linkFocusG);
        linkG.add(linesTop);
        linkG.add(linesBottom);
        linkG.add(linkHitArea);

        root.add(titleMesh);
        root.add(titlePrefixMesh);
        root.add(linkG);
        this.add(root);

        this.titleMesh = titleMesh;
        this.titlePrefixMesh = titlePrefixMesh;
        this.linkHitArea = linkHitArea;
        this.linkMesh = linkMesh;
        this.linkG = linkG;
        this.linkFocusG = linkFocusG;
        this.linesTop = linesTop;
        this.linesBottom = linesBottom;
        this.mainView = root;
        this.mainClickTarget = [linkHitArea];
        this.clickActions = [this.data.action];

        this._updatePositions(this._orientation);
    }

    start() {
        super.start();

        if (this.clickable) {
            const target = this.linkHitArea || this.mainView || this;

            if (this.mainView) this.mainView.layers.set(this._layerChannel);
            if (this.linkG) this.linkG.layers.set(this._layerChannel);
            target.layers.set(this._layerChannel);

            target.on('mouseover', () => {
                this.onOver();
            });
            target.on('mouseout', () => {
                this.onOut();
            });

            this._target = target;
        }
    }

    invalidate(t, dt) {
        super.invalidate(t, dt);

        if (!this._hasOscillation) {
            if (this._hasOscillationChanged) {
                this.linkFocusG.position.x = 0;
                this.linkFocusG.position.y = 0;
                this.linkFocusG.rotation.x = 0;
                this.linkFocusG.rotation.y = 0;
            }

            return;
        }

        this.linkFocusG.position.x = this._amp * 0.4 * Math.sin((t * Math.PI) / 3.4465);
        this.linkFocusG.position.y = this._amp * 0.4 * Math.sin((t * Math.PI) / 4.35245);

        this.linkFocusG.rotation.x = this._amp * radians(10) * Math.sin((t * Math.PI) / 1.937562);
        this.linkFocusG.rotation.y = this._amp * radians(3) * Math.sin((t * Math.PI) / 2.753238);
    }

    focusAt(actionElemIdOrName) {
        if (!this.clickable) return;
        if (this._isUnderFocus) return;
        if (actionElemIdOrName !== this.actionKey) return;

        gsap.to(this.linkFocusG.position, {
            duration: 0.74,
            z: 0.072 * UNIT.zSize
        });

        gsap.to(this, {
            duration: 0.94,
            amp: 1
        });

        this._isUnderFocus = true;
    }

    blur(actionElemIdOrName) {
        if (!this.clickable) return;
        if (actionElemIdOrName !== this.actionKey) return;

        const target = this.linkMesh || this.mainView || this;

        this.onOut(target);

        if (this._focusTl) {
            this._focusTl.kill();
            this._focusTl = null;
        }

        this._isUnderFocus = false;

        gsap.killTweensOf(this.linkFocusG.position);
        gsap.killTweensOf(this);

        gsap.to(this.linkFocusG.position, {
            duration: 0.45,
            z: 0
        });

        gsap.to(this, {
            duration: 0.45,
            amp: 0
        });

    }

    onOver() {
        gsap.killTweensOf(this);

        gsap.to(this, {
            duration: 0.45,
            amp: 0
        });

        this._moveHandler = (evt) => {
            this.onMove(evt);
        };

        this._target.on('mousemove', this._moveHandler);
    }

    onOut() {
        const duration = 0.45;

        gsap.killTweensOf(this.linkMesh.position);
        gsap.killTweensOf(this.linkMesh.rotation);
        gsap.killTweensOf(this);

        gsap.to(this.linkMesh.position, {
            duration,
            x: 0,
            y: 0,
            z: 0
        });

        gsap.to(this.linkMesh.rotation, {
            duration,
            x: 0,
            y: 0,
            z: 0
        });

        if (this._isUnderFocus) {
            gsap.to(this, {
                duration,
                amp: 1
            });
        }
    }

    onMove(evt) {
        this.updateMatrixWorld();

        const { camera } = this._webgl;
        const vectorMouse = evt.data.global;
        let vector = new THREE.Vector3();
        const mtx = new THREE.Matrix4();

        vector.setFromMatrixPosition(this.linkG.matrixWorld);

        vector = vector.applyMatrix4(camera.matrixWorldInverse).applyMatrix4(mtx.copy(camera.projectionMatrix));
        vector.set(
            (vectorMouse.x - vector.x) / 1.2,
            (vectorMouse.y - vector.y) / 1.2,
            0
        );

        this._moveTo(vector);
    }

    _moveTo(vector = null) {
        vector = vector || new THREE.Vector3(0, 0, 0);

        gsap.killTweensOf(this.linkMesh.position);
        gsap.killTweensOf(this.linkMesh.rotation);

        gsap.to(this.linkMesh.position, {
            duration: 0.94,
            ease: 'sine.out',
            x: vector.x * 0.03 * UNIT.zSize,
            y: vector.y * 0.03 * UNIT.zSize,
            z: 0.072 * UNIT.zSize
        });
        gsap.to(this.linkMesh.rotation, {
            duration: 0.94,
            ease: 'sine.out',
            x: (vector.y * 10 * Math.PI) / 180,
            y: (-vector.x * 60 * Math.PI) / 180
        });
    }

    _getTextMesh(metrics, text) {
        if (metrics.isUpperCase) text = text.toUpperCase();

        return new Text3D({
            ...metrics,
            color: this._color
        }, text, this.webgl);
    }

    resize(winWidth, winHeight, orientation, force) {
        if (this._orientation === orientation && !force) return;

        this.titleMesh.setMetrics({
            ...this._getTitleMetrics(orientation)
        });

        this.titlePrefixMesh.setMetrics({
            ...this._getPrefixMetrics(orientation)
        });

        this.linkMesh.setMetrics({
            ...this._getLinkMetrics(orientation)
        });

        this._updatePositions(orientation);

        this._orientation = orientation;
    }

    _updatePositions(sizeOption) {
        this.mainView.position.y = sizeOption === SIZE_OPTIONS.PORTRAIT ? 120 * UNIT.sizesScaleRatio : 0;

        let obj3D = this.titleMesh;

        const dy = obj3D.lineHeight * 0.5;

        obj3D.position.y = obj3D.textHeight * 0.5 + dy;

        const obj3D2 = this.titlePrefixMesh;

        obj3D2.position.y = obj3D2.textHeight * 0.5 + obj3D.textHeight * 0.5 + obj3D.position.y;

        const { textHeight, lineHeight, textWidth } = this.linkMesh;

        obj3D = this.linkG;
        obj3D.position.y = -textHeight * 0.5 - lineHeight * 3 + dy;

        const yFromCenter = textHeight * 0.5 + lineHeight * 0.5;
        let scale = sizeOption === SIZE_OPTIONS.PORTRAIT ? 2 : 1;

        scale *= UNIT.sizesScaleRatio;

        obj3D = this.linesTop;
        obj3D.position.y = yFromCenter;
        obj3D.scale.set(scale, scale, 1);

        obj3D = this.linesBottom;
        obj3D.position.y = -lineHeight * 0.8 - yFromCenter;
        obj3D.scale.set(scale, scale, 1);

        obj3D = this.linkHitArea;
        obj3D.position.y = -yFromCenter * 0.4;
        obj3D.scale.set(textWidth * 0.01, textHeight * 2.5 * 0.01, 1);
    }

    get amp() {
        return this._amp;
    }

    set amp(value) {
        this._amp = value;
        const newHasOscillation = value > 0.001;

        this._hasOscillationChanged = newHasOscillation !== this._hasOscillation;
        this._hasOscillation = newHasOscillation;
    }
}

export default FooterUnit;
